import React from 'react';
import { Card, CardBody } from '@heroui/react';
import { IValueVessel } from '../valueTypes';

type VesselPropertiesProps = {
	vessel: IValueVessel;
};

const VesselProperties: React.FC<VesselPropertiesProps> = ({ vessel }) => {
	const formatKey = (key: string) => {
		const formatted = key.replace(/([A-Z])/g, ' $1').trim();

		// if first word is imo then uppercase it
		if (formatted.toLowerCase().startsWith('imo')) {
			return 'IMO' + formatted.slice(3);
		}

		return formatted.charAt(0).toUpperCase() + formatted.slice(1);
	};

	// Define the keys to display
	const baseFields = [
		'imoNumber',
		'name',
		'sectorName',
		'size',
		'sizeUnit',
		'age',
		'yearOfDelivery',
		'iceClass',
		'countryOfBuild',
		'yardTier',
		'gear',
		'hatch',
		'efficiency',
		'scrubberFitted',
		'fuelSystem',
	];

	const baseFieldsCarTruck = [
		'imoNumber',
		'name',
		'sectorName',
		'size',
		'sizeUnit',
		'yearOfDelivery',
		'iceClass',
		'countryOfBuild',
		'yardTier',
		'hoistableDecks',
		'quarterRampCapacity',
		'scrubberFitted',
		'fuelSystem',
	];

	const baseFieldsMultiPurpose = [
		'imoNumber',
		'name',
		'sectorName',
		'size',
		'sizeUnit',
		'yearOfDelivery',
		'iceClass',
		'countryOfBuild',
		'yardTier',
		'gear',
		'scrubberFitted',
	];

	const baseFieldsOilTanker = [
		'imoNumber',
		'name',
		'sectorName',
		'size',
		'sizeUnit',
		'yearOfDelivery',
		'iceClass',
		'countryOfBuild',
		'yardTier',
		'tank',
		'efficiency',
		'scrubberFitted',
		'pumpRoom',
		'fuelSystem',
	];

	const baseFieldsDryBulk = [
		'imoNumber',
		'name',
		'sectorName',
		'size',
		'sizeUnit',
		'yearOfDelivery',
		'iceClass',
		'countryOfBuild',
		'yardTier',
		'gear',
		'hatch',
		'efficiency',
		'scrubberFitted',
		'fuelSystem',
	];

	const baseFieldsContainer = [
		'imoNumber',
		'name',
		'sectorName',
		'size',
		'sizeUnit',
		'yearOfDelivery',
		'iceClass',
		'countryOfBuild',
		'yardTier',
		'gear',
		'efficiency',
		'scrubberFitted',
		'dwtCapacity',
		'reeferPlugs',
		'homogeneousIntake',
		'eco',
		'fuelSystem',
	];

	const baseFieldsChemical = [
		'imoNumber',
		'name',
		'sectorName',
		'size',
		'sizeUnit',
		'yearOfDelivery',
		'iceClass',
		'countryOfBuild',
		'yardTier',
		'tank',
		'imoClassAndCoating',
	];

	const baseFieldsLNG = [
		'imoNumber',
		'name',
		'sectorName',
		'size',
		'sizeUnit',
		'yearOfDelivery',
		'iceClass',
		'countryOfBuild',
		'yardTier',
		'propulsionSystem',
		'cargoContainment',
	];

	const baseFieldsLPG = [
		'imoNumber',
		'name',
		'sectorName',
		'size',
		'sizeUnit',
		'yearOfDelivery',
		'iceClass',
		'countryOfBuild',
		'yardTier',
		'tank',
		'ammoniaCarrier',
		'scrubberFitted',
		'fuelSystem',
	];

	const fieldsToDisplay =
		vessel.sectorName === 'Car/Truck'
			? baseFieldsCarTruck
			: vessel.sectorName === 'Multi-Purpose'
				? baseFieldsMultiPurpose
				: vessel.sectorName === 'Oil Tanker'
					? baseFieldsOilTanker
					: vessel.sectorName === 'Dry Bulk'
						? baseFieldsDryBulk
						: vessel.sectorName === 'Containership'
							? baseFieldsContainer
							: vessel.sectorName === 'Chemical'
								? baseFieldsChemical
								: vessel.sectorName === 'LNG'
									? baseFieldsLNG
									: vessel.sectorName === 'LPG'
										? baseFieldsLPG
										: baseFields;

	return (
		<Card className="bg-white w-full h-full max-w-md shadow-none border border-msi-darkgrey-color-300 rounded">
			<CardBody>
				<div className="border-t-2 border-b-2 border-msi-midblue-color-500 p-1">
					<div className="grid gap-0 text-sm text-gray-700">
						{fieldsToDisplay.map(
							(key) =>
								vessel[key as keyof IValueVessel] && (
									<div key={key} className="flex justify-between border-b border-dotted pb-1">
										<span className="font-normal text-msi-midblue-color-500">{formatKey(key)}</span>
										<span className='items-center justify-center' >{vessel[key as keyof IValueVessel] ?? '-'}</span>
									</div>
								),
						)}
					</div>
				</div>
			</CardBody>
		</Card>
	);
};

export default VesselProperties;
